import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import 'rxjs/add/operator/switchMap';

import { LabOrderApiService } from './lab-order-api.service';
import { LabOrder } from './data/lab-order';
import { ReferenceListItem } from './data/reference-list-item';
import { LabOrderReferenceApiService, RefListType } from './lab-order-reference-api.service';
import { SearchResult } from './data/search-result';
import { SearchCriteria } from './data/search-criteria';
import {LensEnhancementDescription} from './data/lens-enhancement-description';
import {UtilsService} from './utils.service';


const LAB_MODIFIED_STATUS = 'MODIFIED_23';

@Injectable()
export class LabOrderBusinessService {
  sequence: any;

  lensEnhancements: Map<string, LensEnhancementDescription>;

  constructor(private labOrderApiService: LabOrderApiService, private labOrderReferenceService: LabOrderReferenceApiService) { }

  public initialDataLoad() {

    this.labOrderApiService.getLensEnhancementDescriptions()
      .subscribe(
        (result) => {
          this.lensEnhancements = this.convertLensEnhancementDescriptionsToMap(result);
        },
        (error) => {
          this.lensEnhancements = new Map<string, LensEnhancementDescription>();
          console.warn('Could not get lenses enhancement descriptions from the catalog API');
        }
      );

  }

  // Assign an invoice number onto the a lab order based on a given labOrderNumber
  public assignInvoiceNumber(labOrderNumber: string, labInvoiceNumber: string): Observable<LabOrder> {

    console.log('Business Service assignInvoiceNumber');

    // Get the lab order, set the labInvoiceNumber, update it
    this.sequence = this.labOrderApiService.getLabOrder<LabOrder>(labOrderNumber)
      .switchMap(labOrder => {
        console.log('After GET results ');
        console.log(labOrder);

        labOrder.labInvoiceNumber = labInvoiceNumber;
        labOrder.status = LAB_MODIFIED_STATUS;

        console.log('laborder status:', labOrder.status);
        return this.labOrderApiService.updateLabOrder(labOrder);
      });

    return this.sequence;
  }

  // Accept a lab order based on a given labOrderNumber
  public acceptLabOrder(labOrderNumber: string, labInvoiceNumber?: string): Observable<LabOrder> {

    console.log('Business Service acceptLabOrder');

    // Get the lab order, set the status to LAB_ACCEPTED_21, update it
    this.sequence = this.labOrderApiService.getLabOrder<LabOrder>(labOrderNumber)
      .switchMap(labOrder => {
        console.log('After GET results ');
        labOrder.status = 'LAB_ACCEPTED_21';
        labOrder.labInvoiceNumber = labInvoiceNumber;
        return this.labOrderApiService.updateLabOrder(labOrder);
      });

    return this.sequence;
  }

  // Void a lab order based on a given labOrderNumber
  public voidLabOrder(labOrderNumber: string, labInvoiceNumber: string): Observable<LabOrder> {

    console.log('Business Service voidLabOrder');

    // Get the lab order, set the status to LAB_VOIDED_04, update it
    this.sequence = this.labOrderApiService.getLabOrder<LabOrder>(labOrderNumber)
      .switchMap(labOrder => {
        labOrder.labInvoiceNumber = labInvoiceNumber;
        labOrder.status = 'LAB_VOIDED_04';
        return this.labOrderApiService.updateLabOrder(labOrder);
      });

    return this.sequence;
  }

  public completeLabOrder(labOrderNumber: string, labInvoiceNumber?: string): Observable<LabOrder> {
    return this.labOrderApiService.getLabOrder<LabOrder>(labOrderNumber)
      .switchMap(labOrder => {
        console.log('CompleteLabOrder: After GET results: \n', labOrder);

        // if it is in a modified status, first Accept it, then complete it. Modified to Complete is
        // an invalid state transition on the API.
        if (labOrder.status === LAB_MODIFIED_STATUS ) {
          console.log('CompleteLabOrder: order is in modified state. Need to accept first');

          labOrder.status = 'LAB_ACCEPTED_21';

          return this.labOrderApiService.updateLabOrder(labOrder)
            .switchMap(acceptedlabOrder => {
              console.log('CompleteLabOrder: order transitioned from Modified to Accepted order, next up is complete.');

              return this.completeAndUpdate(labOrderNumber, acceptedlabOrder, labInvoiceNumber);
            })
            .catch(error => {
              console.log('uh oh, update order failed:', error);
              return Observable.throw(error);
            });
        } else {
          console.log('CompleteLabOrder: complete order, assign invoice if applicable');
          return this.completeAndUpdate(labOrderNumber, labOrder, labInvoiceNumber);
        }
      });
  }

  private completeAndUpdate(labOrderNumber: string, labOrder: LabOrder, labInvoiceNumber?: string): Observable<LabOrder> {
      labOrder.status = this.labOrderCompleteStatus(labOrder);
      labOrder.labInvoiceNumber = labInvoiceNumber;
      return this.labOrderApiService.updateLabOrder(labOrder);
    }

  // get all reference lists to populate form drop-down.
  public getReferenceLists() {

    console.log('Business Service getReferenceLists');

    // Get all reference lists.
    this.sequence = this.labOrderApiService.getReferenceLists();

    return this.sequence;
  }

  // get specified reference lists.
  public getReferenceListsByNames(referenceLists: RefListType[]) {

    console.log('Business Service getSomeReferenceLists');

    // Get all reference lists.
    this.sequence = this.labOrderReferenceService.getReferenceListsByNames(referenceLists);

    return this.sequence;
  }

  // view a lab order based on a given labOrderNumber
  public viewLabOrder(labOrderNumber: string): Observable<LabOrder> {

    console.log('Business Service viewLabOrder');

    // Get the lab order
    this.sequence = this.labOrderApiService.getLabOrder<LabOrder>(labOrderNumber);

    return this.sequence;
  }

  // view a lab order based on a given URL
  public viewLabOrderByUrl(url: string): Observable<LabOrder> {

    console.log('Business Service viewLabOrderByUrl');

    // Get the lab order
    this.sequence = this.labOrderApiService.getLabOrderByUrl<LabOrder>(url);

    return this.sequence;
  }

  // view a lab order based on a given labOrderNumber
  public modifyLabOrder(labOrder: LabOrder, labInvoiceNumber: string): Observable<LabOrder> {

    // The API currently does not allow updates that do not also change state.  Changing
    // the status to MODIFIED_23 will allow the change and then automatically re-transition
    // back to LAB_ACCEPTED_21.
    labOrder.status = 'MODIFIED_23';
    labOrder.labInvoiceNumber = labInvoiceNumber;

    console.log('In modifyLabOrder Business Service');

    this.sequence = this.labOrderApiService.updateLabOrder(labOrder);

    return this.sequence;
  }

  public labSearch(searchCriteria: SearchCriteria): Observable<SearchResult> {
    console.log('In LabSearch Business Service');

    this.sequence = this.labOrderApiService.labSearch(searchCriteria);

    return this.sequence;
  }

  public labSearchByUrl(url: string): Observable<SearchResult> {
    console.log('In LabSearchByUrl Business Service');

    this.sequence = this.labOrderApiService.labSearchByUrl(url);

    return this.sequence;
  }

  public labOrderCompleteStatus(labOrder: LabOrder): string {
    if (UtilsService.isPrivateOrder(labOrder)) {
      console.log('private order');
      return 'COMPLETED_PRIVATE_ORDER_98';
    }
    console.log('VSP order');
    return 'CLAIM_READY_COMPLETED_VSP_ORDER_27';
  }

  public prettyName(ref: string): string {

    // Make sure we have values to work with
    let prettyValue = '';
    if (ref) {
      // If it's a list of strings in a format like "SOME_CODE_VALUE".
      if (typeof ref === 'string') {
          const tokens = ref.split('_');
          let first = true;
          tokens.forEach((token) => {
            prettyValue = prettyValue + (first ? '' : ' ') + this.toProperCase(token);
            first = false;
          });

      } else {
        // Assume that if it's anything other than a list of strings, it doesn't need prettification.
        prettyValue = ref;
      }
    }

    return prettyValue;
  }

  // prettify the values from web-services to display to user.
  // add an 'none' option to the reference list.
  public prettifyRefList(data: ReferenceListItem[], addNoneOption: ReferenceListItem = null,
                         filter: string[] = []): ReferenceListItem[]  {
    const prettyItems: ReferenceListItem[] = [];

    // If addNoneOption == true then
    // add an empty selection at the beginning
    if (addNoneOption) {
      prettyItems.push(addNoneOption);
    }

    if (data) {
      // get out names
      data.forEach(item => {
        if (filter.indexOf(item.id) === -1) {
          prettyItems.push({id: item.id, name: this.prettyName(item.name)});
        }
      });
    }

    return prettyItems;
  }


  public getNewEmptyReferenceListItem() {
    const newItem: ReferenceListItem = { id: '', name: '' };
    return newItem;
  }

  private toProperCase(string: String) {
    return string.replace(/\w\S*/g,
      function(txt){return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(); });
  }

  private convertLensEnhancementDescriptionsToMap(
    lensEnhancementDescriptions: LensEnhancementDescription[]): Map<string, LensEnhancementDescription> {

    const retVal: Map<string, LensEnhancementDescription> = new Map<string, LensEnhancementDescription>();

    if (lensEnhancementDescriptions) {
      lensEnhancementDescriptions.forEach(lensEnhancement => {
        retVal.set(lensEnhancement.code, lensEnhancement);
      });
    }

    return retVal;
  }

  public getLensEnhancements(): Map<string, LensEnhancementDescription> {
    return this.lensEnhancements;
  }
}



// WEBPACK FOOTER //
// /home/jenkins-agent/workspace/ng-labportal-sandbox-build/src/app/shared/lab-order-business.service.ts